import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';

export interface IDedDetailByLicenseData {
  id: number;
  englishName?: string;
  arabicName?: string;
  pageUrl?: string;
  businessName: string;
  licenseStatus: string;
  legalType?: string;
  expiryDate?: string;
  activities?: string;
  telephone?: string;
  fax?: string;
  mobile?: string;
  poBox?: string;
  email?: string;
  address?: string;
  area?: string;
  subArea?: string;
  community?: string;
  landmark?: string;
  parcelId?: string;
  street?: string;
  building?: string;
  floor?: string;
  unit?: string;
  gpsCoordinates?: string;
  licenseNumber: string;
  licenseIssuedAt?: string;
  createdAt: string;
  updatedAt: string;
  brandLicense?: {
    id: number;
    brandId: number;
    licenceId: number;
  } | null;
  // Enhanced UX fields
  isExpiringSoon: boolean;
  hasCompleteContactInfo: boolean;
  hasCompleteAddress: boolean;
  isActive: boolean;
}

const endpoint = 'ums/v1/crm/management/ded/licenses/license';

type TData = IDedDetailByLicenseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, string];

type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  enabled?: boolean;
};
type ReturnType = UseQueryResult<TData, TError>;

export function useGetDedDetailByLicenseNumberApi(licenseNumber: string, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { enabled: userEnabled = true, ...restOptions } = options || {};

  const queryKey: TQueryKey = [endpoint, licenseNumber];

  const queryFn = async (): Promise<TData> => {
    const { data } = await http.get(`${endpoint}/${encodeURIComponent(licenseNumber)}`);
    return data;
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn && userEnabled && !!licenseNumber,
    ...restOptions,
  });
}
